// declare these as constants in /constants/ later
import ERRORTYPES from '../constants/errorTypes';
import validationRules from '../constants/validationRules';

/* //
    HELPER FUNCTIONS
 */

// validates length based on min max length (not inclusive)
function validateLength(min, max, value) {
  return typeof value === 'string' && value.length > min && value.length < max;
}

function checkValidEmail(email) {
  // General Email Regex (RFC 5322 Official Standard) from http://emailregex.com/
  const validEmail = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return validEmail.test(email);
}

function checkHasLetter(value) {
  const hasLetter = new RegExp('^(?=.*[a-zA-Z])');
  return hasLetter.test(value);
}

function checkHasDigit(value) {
  const hasDigit = new RegExp('^(?=.*[0-9])');
  return hasDigit.test(value);
}

function checkIsAscii(value) {
  return !/[^\x00-\x7F]+/.test(value);
}

function assembleValidationErrorString(validationArr, type) {
  return `${type === 'password' ? ERRORTYPES.PASSWORD_PREFIX : ERRORTYPES.USERNAME_PREFIX} ${validationArr.join(' and')}.`;
}

export function checkMatching(val1, val2) {
  return val2 !== undefined && val1 !== undefined && val1 === val2;
}

// validates email and generates dynamic error message if invalid
export function validateEmail(email) {
  const response = {
    isValid: false,
    errorType: ERRORTYPES.NO_TYPE_SET,
  };

  if (email === '') {
    response.errorType = ERRORTYPES.EMAIL_DEFAULT;
    return response;
  }

  response.isValid = validateLength(validationRules.EMAIL_LENGTH_MIN, validationRules.EMAIL_LENGTH_MAX, email)
    && checkValidEmail(email);

  if (!response.isValid) {
    response.errorType = ERRORTYPES.EMAIL_INVALID;
    if (email.length <= validationRules.EMAIL_LENGTH_MIN) {
      response.errorType = ERRORTYPES.EMAIL_SHORT;
    } else if (email.length >= validationRules.EMAIL_LENGTH_MAX) {
      response.errorType = ERRORTYPES.EMAIL_LONG;
    }
  }

  return response;
}

// validates username and generates dynamic error message if invalid
export function validateUsername(username) {
  const response = {
    isValid: false,
    errorType: ERRORTYPES.NO_TYPE_SET,
  };
  if (username === undefined || username === '') {
    response.errorType = `${ERRORTYPES.USERNAME_PREFIX} ${ERRORTYPES.USERNAME_DEFAULT}`;
    return response;
  }

  const isSendGridDomain = ((username.toString()).match(/\@sendgrid\.+[a-zA-Z]{2,}$/));
  const hasSpace = /\s/g.test(username);
  const isValidEmailAddress = checkValidEmail(username);

  response.isValid = validateLength(
    validationRules.USERNAME_LENGTH_MIN,
    validationRules.USERNAME_LENGTH_MAX,
    username,
  )
    && checkHasLetter(username)
    && checkIsAscii(username)
    && !isSendGridDomain
    && !hasSpace
    && !isValidEmailAddress;

  if (!response.isValid) {
    const validationMsgArr = [];

    if (username.length <= validationRules.USERNAME_LENGTH_MIN) {
      validationMsgArr.push(ERRORTYPES.USERNAME_SHORT);
    }
    if (username.length >= validationRules.USERNAME_LENGTH_MAX) {
      validationMsgArr.push(ERRORTYPES.USERNAME_LONG);
    }
    if (!checkHasLetter(username)) {
      validationMsgArr.push(ERRORTYPES.USERNAME_NO_LETTER);
    }
    if (!checkIsAscii(username)) {
      validationMsgArr.push(ERRORTYPES.USERNAME_INVALID_CHARACTERS);
    }
    if (isSendGridDomain) {
      validationMsgArr.push(ERRORTYPES.USERNAME_SENDGRID_DOMAIN);
    }
    if (hasSpace) {
      validationMsgArr.push(ERRORTYPES.USERNAME_SPACE);
    }
    if (isValidEmailAddress && !isSendGridDomain) {
      validationMsgArr.push(ERRORTYPES.USERNAME_EMAIL_ADDRESS);
    }
    if (validationMsgArr.length === 0) {
      validationMsgArr.push(ERRORTYPES.USERNAME_DEFAULT);
    }

    response.errorType = assembleValidationErrorString(validationMsgArr, 'username');
  }
  return (response);
}

// validates confirm password and generates error message if invalid
export function validateConfirmPassword(confirmPassword, password) {
  const response = {
    isValid: false,
    errorType: ERRORTYPES.NO_TYPE_SET,
  };

  if (confirmPassword === '') {
    response.errorType = ERRORTYPES.PASSWORD_CONFIRM;
    return response;
  }

  response.isValid = confirmPassword === password;

  if (!response.isValid) {
    response.errorType = ERRORTYPES.PASSWORD_NO_MATCH;
  }

  return response;
}

// validates password and generates error message if invalid
export function validatePassword(password, confirmPassword, username) {
  const response = {
    isValid: false,
    errorType: ERRORTYPES.NO_TYPE_SET,
    confirmPassword_isValid: false,
    confirmPassword_errorType: ERRORTYPES.NO_TYPE_SET,
  };
  if (!password || password === '') {
    response.errorType = `${ERRORTYPES.PASSWORD_PREFIX} ${ERRORTYPES.PASSWORD_DEFAULT}`;
    return response;
  }
  const usernameAndPasswordMatch = checkMatching(username, password);

  response.isValid = validateLength(
    validationRules.PASSWORD_LENGTH_MIN,
    validationRules.PASSWORD_LENGTH_MAX, password,
  )
    && checkIsAscii(password)
    && checkHasDigit(password)
    && checkHasLetter(password)
    && !usernameAndPasswordMatch;

  if (!response.isValid) {
    const validationMsgArr = [];
    if (password.length <= validationRules.PASSWORD_LENGTH_MIN) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_SHORT);
    } if (!checkHasDigit(password)) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_NO_DIGIT);
    } if (!checkHasLetter(password)) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_NO_LETTER);
    } if (password.length >= validationRules.PASSWORD_LENGTH_MAX) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_LONG);
    } if (!checkIsAscii(password)) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_NON_ASCII);
    } if (usernameAndPasswordMatch) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_MATCH_USERNAME);
    } if (validationMsgArr.length === 0) {
      validationMsgArr.push(ERRORTYPES.PASSWORD_DEFAULT);
    }

    response.errorType = assembleValidationErrorString(validationMsgArr, 'password');
  }

  // update confirm password message on password value change
  const confirmPasswordCheck = validateConfirmPassword(confirmPassword, password);
  response.confirmPassword_errorType = confirmPasswordCheck.errorType;
  response.confirmPassword_isValid = confirmPasswordCheck.isValid;

  return response;
}
